import React from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import { getRoleType } from "services/TenantSubmitHandler";
import { getNestedError } from "utils/splitDot";

interface InputPhoneProps {
  name?: string;
  country?: string;
  rules?: any; // validation rules
  label: string;
  disableForRole?: string[]; // roles for which the field should be disabled
  smsNotification?: boolean; // whether SMS notification is enabled
  isTenantForm?: boolean; // new prop to indicate if it's in tenant form
  // Name of the checkbox (or any field) that controls whether this phone is required
  requiredWhenCheckedField?: string;
  // The value of the controller field that triggers the phone to be required (default: true)
  requiredWhenCheckedValue?: any;
}

const InputPhone: React.FC<InputPhoneProps> = ({
  name = "phone",
  country = "au",
  rules = {
    validate: {
      isAustralian: (value: string) => {
        // Accept only numbers starting with +614 or 04
        if (!value) return 'Phone number is required';
        const auPattern = /^(\+614|04)\d{8}$/;
        return auPattern.test(value.replace(/\s+/g, '')) || 'Enter a valid Australian mobile number starting with +614 or 04';
      }
    }
  },
  label = "",
  disableForRole = [],
  smsNotification = false,
  isTenantForm = false // new prop to indicate if it's in tenant form
  ,
  requiredWhenCheckedField,
  requiredWhenCheckedValue = true
}) => {
  // Consolidate form context usage and watch the controlling field
  const { control, formState } = useFormContext();
  const errors = formState?.errors || {};

  const errorMessage =
    name && getNestedError(errors, name)?.message
      ? getNestedError(errors, name).message
      : undefined;

  // Watch the controlling checkbox/field to determine if phone is required.
  // Only call useWatch when a field name is provided to satisfy the hook's typing.
  // useWatch must be called unconditionally; cast name to any to satisfy overloads when undefined
  const watchedValue = useWatch({ control, name: requiredWhenCheckedField as any });
  const isRequired = requiredWhenCheckedField
    ? watchedValue === requiredWhenCheckedValue
    : false;

  const touched = formState.touchedFields?.[name as string];

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        ...rules,
        validate: {
          ...(rules?.validate || {}),
          isAustralian: (value: string) => {
            // If the phone is required by the controlling field, enforce presence + pattern.
            // Otherwise, allow empty (unless smsNotification forces validation elsewhere).
            const auPattern = /^(\+614|04)\d{8}$/;

            if (isRequired) {
              // required: must be present and match pattern
              if (!value || value === "+61") return 'Phone number is required';
              return auPattern.test(value.replace(/\s+/g, '')) || 'Enter a valid Australian mobile number starting with +614 or 04';
            }

            // not required: only validate if a value is provided (or smsNotification logic)
            if ((!value || value === "+61") && !smsNotification) return true;
            return auPattern.test(value.replace(/\s+/g, '')) || 'Enter a valid Australian mobile number starting with +614 or 04';
          }
        }
      }}
      render={({ field }) => (
        <div className="d-flex flex-column gap-10px">
          <label className="d-block fs-16px fw-normal text-black">
            {label || ""} {!isRequired && requiredWhenCheckedField && " (Optional)" }
          </label>
          <PhoneInput
            {...field}
            defaultCountry={country}
            inputClassName="input-field input-field-editable flex-fill"
            value={field.value || ""}
                onChange={(val) => {
                  let newVal = val;
                  if (country === "au") {
                    // Remove leading '0' after '+61' or '+61 '
                    newVal = newVal.replace(/^(\+61\s*0)/, '+61 ');
                    // Also handle if user types just '0' at the start
                    if (newVal.startsWith('0')) {
                      newVal = newVal.substring(1);
                    }
                  }
                  field.onChange(newVal);
                }}
            disabled={disableForRole.includes(getRoleType())} // Example: disable for 'landlord' role
          />
          {errorMessage && touched && (
            <div className="text-red fs-14px">{errorMessage}</div>
          )}
        </div>
      )}
    />
  );
};

export default InputPhone;
